import React, { useState } from "react";
import { Divider, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import TabbedDialog from "components/dialogs/TabbedDialog";
import TopicsTreeMapPreview from "./TopicsTreeMapPreview";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import TopicQuerySettings from "components/widgets/utility/TopicQuerySettings";
import { ClusteringBias, normalizeClusteringBias } from "components/widgets/utility/ClusteringBias";
import { treeMapDefaultSettings, TreeMapSettingsStateType } from "./treeMapUtils";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, getSingleWidgetResponseByName, WidgetRequest } from "system/widget/Widget";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import { Topic } from "./TopicsTreeMap";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";

const defaultWidgetRequest = {
  name: 'topics-tree-map',
  endpoint: 'query/topics',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultTopicTreeRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    topics: {
      size: 25,
      types: ["topic"]
    }
   };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export interface TopicTreeSettings {
  topicCount: number;
}

export default function TopicTreeSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'topics-tree-map');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'topics-tree-map');
  const requestBody = originalRequest?.requestInit?.body;

  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>(requestBody?.topics?.fields || []);
  const [selectedTopicsTypes, setSelectedTopicsTypes] = useState<string[]>(requestBody?.topics?.types || []);
  const [queryTermExclusion, setQueryTermExclusion] = useState<string>(requestBody?.topics?.query_term_exclusion || "all");
  const [clusteringBias, setClusteringBias] = useState<ClusteringBias>(normalizeClusteringBias(props.widget?.data?.clusteringBias));
  const [excludedTerms, setExcludedTerms] = useState<string[]>(props.widget?.data?.excludedTerms || []);
  const [bulkExclusionDialogIsOpen, setBulkExclusionDialogIsOpen] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>(requestBody?.topics?.sort || props.widget?.data?.sortBy || "relevance");

  const apiData = apiResponseInfo?.responseBody?.topics ? apiResponseInfo?.responseBody?.topics.map((topic: Topic) => topic.label).sort() : [];

  const topicCountOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 15, label: "15" },
    { value: 20, label: "20" },
    { value: 25, label: `25 (${i18n.t("default")})` },
    { value: 50, label: `50 (${i18n.t("widget-settings.no-preview")})` },
    { value: 75, label: `75 (${i18n.t("widget-settings.no-preview")})` },
    { value: 100, label: `100 (${i18n.t("widget-settings.no-preview")})` }
  ];

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody: {
      topics: {
        size: number,
        types?: string[],
        fields?: string[],
        exclude?: string[],
        query_term_exclusion?: string, // "required" | "none" | "only"
        clustering_bias?: number,
        sort?: string,
      },
    } = {
      topics: {
        size: settingsState.topicCount,
      }
    };
    if (selectedTopicsTypes.length > 0) {
      requestInitBody.topics.types = selectedTopicsTypes;
    }
    if (selectedFieldIds.length > 0) {
      requestInitBody.topics.fields = selectedFieldIds;
    }
    if (excludedTerms.length > 0) {
      requestInitBody.topics.exclude = excludedTerms;
    }
    if (queryTermExclusion !== "all") {
      requestInitBody.topics.query_term_exclusion = queryTermExclusion;
    }
    requestInitBody.topics.clustering_bias = clusteringBias;
    if (sortBy !== "relevance") {
      requestInitBody.topics.sort = sortBy;
    }

    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({
      clusteringBias: clusteringBias,
      excludedTerms: excludedTerms,
      sortBy: sortBy,
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  const [settingsState, setSettingsState] = useState<TreeMapSettingsStateType>({...treeMapDefaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: TreeMapSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  function handleSetSelectedFieldIds(value: string[]) {
    setSelectedFieldIds(value);
  }

  function handleSelectedTopicsTypes(value: string[]) {
    setSelectedTopicsTypes(value);
  }

  function handleSetExcludedTerms(values: string[]) {
    setExcludedTerms(values);
  }

  function handleSetBulkExclusionDialogIsOpen(value: boolean) {
    setBulkExclusionDialogIsOpen(value);
  }

  function handleSetQueryTermExclusion(value: string) {
    setQueryTermExclusion(value);
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false) }
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.topics-tree-map.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <TopicQuerySettings
              apiData={apiData}
              bulkExclusionDialogIsOpen={bulkExclusionDialogIsOpen}
              clusteringBias={clusteringBias}
              excludedTerms={excludedTerms}
              queryInfo={props.queryInfo}
              queryTermExclusion={queryTermExclusion}
              selectedFieldIds={selectedFieldIds}
              selectedTopicsTypes={selectedTopicsTypes}
              sortBy={sortBy}
              setBulkExclusionDialogIsOpen={handleSetBulkExclusionDialogIsOpen}
              setClusteringBias={setClusteringBias}
              setExcludedTerms={handleSetExcludedTerms}
              setQueryTermExclusion={handleSetQueryTermExclusion}
              setSelectedFieldIds={handleSetSelectedFieldIds}
              setSelectedTopicsTypes={handleSelectedTopicsTypes}
              setSortBy={setSortBy}
            />
          </>)}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <TopicsTreeMapPreview settingsState={settingsState} />

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                {/* Widget Title */}
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>
                }
              </SettingsAccordion>
              {/* Topic Count */}
              <SettingsSingleSelect
                defaultValue={topicCountOptions.find((item: OptionsValueType) => {
                  return item.value === treeMapDefaultSettings.topicCount;
                }) ?? topicCountOptions[0]}
                keyid="topicCount"
                label={i18n.t("widget-types.topics-tree-map.settings.topic-count")}
                options={topicCountOptions}
                value={topicCountOptions.find((item: OptionsValueType) => {
                  return item.value === settingsState.topicCount;
                }) ?? topicCountOptions[0]} />
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>
    </TabbedDialog>
  )
}
